import React from "react";
import {
  MONTH_NAMES,
  type MonthName,
  type FilterType,
  type FilterControlsProps,
} from "../types";

const FilterControls: React.FC<FilterControlsProps> = ({
  hemisphere,
  currentSort,
  currentMonth,
  currentFilter,
  searchTerm,
  scheduleFilter,
  onHemisphereChange,
  onSortChange,
  onMonthChange,
  onFilterChange,
  onSearchChange,
  onScheduleFilterChange,
  showScheduleButtons,
}) => {
  return (
    <>
      <style>{`
        .btn-compact {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-xs);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-surface);
          color: var(--color-text-secondary);
          cursor: pointer;
          transition: all var(--transition-fast);
          min-width: 28px;
          height: 28px;
        }

        .btn-compact:hover {
          background: var(--color-surface-hover);
          border-color: var(--color-border-strong);
          color: var(--color-text-primary);
        }

        .btn-compact.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: var(--color-text-inverse);
        }

        .btn-compact.active.northern {
          background: var(--color-northern);
          border-color: var(--color-northern);
        }

        .btn-compact.active.southern {
          background: var(--color-southern);
          border-color: var(--color-southern);
        }

        .btn-compact.active.success {
          background: var(--color-success);
          border-color: var(--color-success);
        }

        .btn-compact.active.warning {
          background: var(--color-warning);
          border-color: var(--color-warning);
        }

        @media (max-width: 1024px) {
          .compact-filters {
            gap: var(--space-sm) !important;
          }
        }

        @media (max-width: 768px) {
          .compact-filters {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .filter-group {
            justify-content: space-between !important;
          }
        }
      `}</style>

      <div
        className="compact-filters"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--space-md)",
          alignItems: "center",
          padding: "var(--space-md)",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        {/* Month Selection - Compact */}
        <div
          className="filter-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
          }}
        >
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              minWidth: "35px",
            }}
          >
            Month:
          </span>
          <select
            value={currentMonth === "AllMonths" ? "" : currentMonth}
            onChange={(e) =>
              onMonthChange(
                e.target.value ? (e.target.value as MonthName) : "AllMonths",
              )
            }
            style={{
              padding: "var(--space-xs) var(--space-sm)",
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-surface)",
              color: "var(--color-text-primary)",
              cursor: "pointer",
              minWidth: "100px",
            }}
          >
            <option value="">All Months</option>
            {MONTH_NAMES.map((month) => (
              <option key={month} value={month}>
                {month.slice(0, 3)}
              </option>
            ))}
          </select>
        </div>

        {/* Hemisphere Selection - Compact */}
        <div
          className="filter-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
          }}
        >
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              minWidth: "fit-content",
            }}
          >
            Hemisphere:
          </span>
          <div style={{ display: "flex", gap: "var(--space-xs)" }}>
            <button
              className={`btn-compact ${
                hemisphere === "northern" ? "active northern" : ""
              }`}
              onClick={() => onHemisphereChange("northern")}
            >
              🌎 N
            </button>
            <button
              className={`btn-compact ${
                hemisphere === "southern" ? "active southern" : ""
              }`}
              onClick={() => onHemisphereChange("southern")}
            >
              🌏 S
            </button>
          </div>
        </div>

        {/* Category Filter - Compact */}
        <div
          className="filter-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
          }}
        >
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              minWidth: "fit-content",
            }}
          >
            Type:
          </span>
          <div style={{ display: "flex", gap: "var(--space-xs)" }}>
            {(
              [
                "All",
                "Bug",
                "Fish",
                "Ocean",
                "River",
                "Pier",
                "Pond",
              ] as FilterType[]
            ).map((type) => (
              <button
                key={type}
                className={`btn-compact ${
                  currentFilter === type ? "active" : ""
                }`}
                onClick={() => onFilterChange(type)}
                title={
                  type === "All" ? "All Items" : type === "Bug" ? "Bugs" : type
                }
              >
                {getCompactTypeIcon(type)}
              </button>
            ))}
          </div>
        </div>

        {/* Sort Controls - Compact */}
        <div
          className="filter-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
          }}
        >
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              minWidth: "fit-content",
            }}
          >
            Sort:
          </span>
          <div style={{ display: "flex", gap: "var(--space-xs)" }}>
            <button
              className={`btn-compact ${
                currentSort === "ascName" ? "active" : ""
              }`}
              onClick={() => onSortChange("ascName")}
              title="Name A-Z"
            >
              🔤↑
            </button>
            <button
              className={`btn-compact ${
                currentSort === "descName" ? "active" : ""
              }`}
              onClick={() => onSortChange("descName")}
              title="Name Z-A"
            >
              🔤↓
            </button>
            <button
              className={`btn-compact ${
                currentSort === "ascPrice" ? "active" : ""
              }`}
              onClick={() => onSortChange("ascPrice")}
              title="Price Low-High"
            >
              💰↑
            </button>
            <button
              className={`btn-compact ${
                currentSort === "descPrice" ? "active" : ""
              }`}
              onClick={() => onSortChange("descPrice")}
              title="Price High-Low"
            >
              💰↓
            </button>
          </div>
        </div>

        {/* Search - Compact */}
        <div
          className="filter-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
            flex: "1",
            minWidth: "200px",
          }}
        >
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              minWidth: "fit-content",
            }}
          >
            Search:
          </span>
          <div style={{ position: "relative", flex: "1" }}>
            <input
              type="text"
              placeholder="Name, type, location..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onSearchChange(e.target.value)
              }
              style={{
                width: "100%",
                padding: "var(--space-xs) var(--space-sm)",
                fontSize: "var(--font-size-xs)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-sm)",
                background: "var(--color-surface)",
                color: "var(--color-text-primary)",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* Schedule Filters - Compact */}
        {showScheduleButtons && (
          <div
            className="filter-group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-xs)",
            }}
          >
            <span
              style={{
                fontSize: "var(--font-size-xs)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-secondary)",
                minWidth: "fit-content",
              }}
            >
              Schedule:
            </span>
            <div style={{ display: "flex", gap: "var(--space-xs)" }}>
              <button
                className={`btn-compact ${
                  scheduleFilter === "new" ? "active success" : ""
                }`}
                onClick={() => onScheduleFilterChange("new")}
                title="New This Month"
              >
                ✨
              </button>
              <button
                className={`btn-compact ${
                  scheduleFilter === "end" ? "active warning" : ""
                }`}
                onClick={() => onScheduleFilterChange("end")}
                title="Ending This Month"
              >
                ⏰
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const getCompactTypeIcon = (type: FilterType): string => {
  switch (type) {
    case "All":
      return "📋";
    case "Bug":
      return "🐛";
    case "Fish":
      return "🐟";
    case "Ocean":
      return "🌊";
    case "River":
      return "🏞️";
    case "Pier":
      return "🏗️";
    case "Pond":
      return "🪷";
    default:
      return "📋";
  }
};

export default FilterControls;
